import { adjustmentLinesSum } from './adjustment-note-payload';
import {
  ADJUSTMENT_LINES_SUM_TOLERANCE,
  usesSimpleLayout,
  type AdjustmentNoteSubmitValues,
} from './adjustment-note-schema';
import type { ValueChangeState } from './adjustment-value-change';
import type { InvoiceRow } from '../types';

// Everything that still blocks saving a Credit/Debit Note, in the operator's
// language — the dialog footer shows these next to the disabled button so a
// blocked create is never unexplained. Empty means the save gate is open.

export interface SaveBlocksArgs {
  readonly noteType: 'Credit' | 'Debit';
  readonly side: 'buy' | 'sell';
  readonly selectedId: string;
  readonly totalAmount: number;
  readonly lines: AdjustmentNoteSubmitValues['adjustment_lines'];
  readonly parentsLoading: boolean;
  readonly parents: InvoiceRow[];
  readonly reason?: string | undefined;
  readonly settlement?: string | undefined;
  // Follow-on path: the parent is fixed and its field disabled, so a missing
  // parent needs a different explanation than "select one".
  readonly parentLocked?: boolean;
}

export function adjustmentNoteBlockers(
  save: SaveBlocksArgs,
  valueChange: ValueChangeState,
): string[] {
  return [...saveBlockers(save), ...valueChangeBlockers(valueChange)];
}

export function saveBlockers(args: SaveBlocksArgs): string[] {
  const blockers: string[] = [];
  if (!args.parentsLoading && args.parents.length === 0) {
    blockers.push(
      'No eligible parent invoice on this side — a note can only adjust an issued invoice.',
    );
  } else if (args.selectedId === '') {
    blockers.push(missingParentBlocker(args));
  }
  // The buy-side simple layout carries a single total — nothing more to gate.
  if (usesSimpleLayout(args.noteType, args.side)) return blockers;
  return [...blockers, ...sellSideBlockers(args)];
}

function missingParentBlocker(args: SaveBlocksArgs): string {
  if (args.parentsLoading) return 'Loading the eligible parent invoices…';
  if (args.parentLocked === true) {
    return 'The invoice this note was started from is not adjustable — only an issued invoice (not a draft, void, or cancelled one) can take a note.';
  }
  return 'Select a parent invoice.';
}

// Sell-side notes itemise the charge — at least one line, a positive total, a
// line sum that matches the total, and (Launchpad parity) a reason and a
// settlement method.
function sellSideBlockers(args: SaveBlocksArgs): string[] {
  const blockers: string[] = [];
  if (args.lines === undefined || args.lines.length === 0) {
    blockers.push('Add at least one line item.');
  } else if (args.totalAmount <= 0) {
    blockers.push('The note total must be greater than zero.');
  } else if (
    Math.abs(adjustmentLinesSum(args.lines) - args.totalAmount) > ADJUSTMENT_LINES_SUM_TOLERANCE
  ) {
    blockers.push('The line totals must add up to the note total.');
  }
  if (isBlank(args.reason)) blockers.push('Enter a reason.');
  if (isBlank(args.settlement)) blockers.push('Pick a settlement method.');
  return blockers;
}

// The footer line for a blocked declared change — the section body already
// carries the detailed banner; this echoes it next to the disabled button.
export function valueChangeBlockers(state: ValueChangeState): string[] {
  if (!state.blocked) return [];
  if (state.mismatch && state.computed !== null) {
    return [
      `The declared change implies a ${state.computed.impliedKind} Note — switch the kind or clear "What changed?".`,
    ];
  }
  if (state.currentPrice === null || state.currentWeight === null) {
    return [
      'No current price/weight on record for this BL side — record core values first, or set "What changed?" back to manual.',
    ];
  }
  return ['Enter the new value for the declared change, or set "What changed?" back to manual.'];
}

function isBlank(value: string | undefined): boolean {
  return value === undefined || value.trim() === '';
}
